import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, User, UserPlus, FileText, Shield } from "lucide-react";
import { api } from "../lib/api";

// Debounced global search over customers, leads, loans, insurance.
export default function GlobalSearch() {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const boxRef = useRef(null);

  useEffect(() => {
    if (!q || q.trim().length < 2) { setResults(null); return; }
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const { data } = await api.get("/search/global", { params: { q } });
        setResults(data);
      } catch { setResults({ customers: [], leads: [], loans: [], insurance: [] }); }
      finally { setBusy(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const go = (path) => {
    setOpen(false); setQ(""); setResults(null); navigate(path);
  };

  const total = results
    ? (results.customers?.length || 0) + (results.leads?.length || 0) + (results.loans?.length || 0) + (results.insurance?.length || 0)
    : 0;

  return (
    <div ref={boxRef} className="relative w-full max-w-xl">
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" />
        <input
          type="search"
          value={q}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          placeholder="Search mobile · Lead ID · Customer ID · Loan File · Vehicle No · Aadhaar · PAN"
          className="w-full h-9 pl-9 pr-8 rounded-full bg-white border border-[#E4E4E7] text-sm focus:outline-none focus:border-[#0052FF] lj-focus"
          data-testid="global-search-input"
        />
        {q && (
          <button onClick={() => { setQ(""); setResults(null); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#52525B] hover:text-[#06152D]" aria-label="Clear">
            <X size={14} />
          </button>
        )}
      </div>

      {open && q.trim().length >= 2 && (
        <div className="absolute mt-2 left-0 right-0 bg-white border border-[#E4E4E7] rounded-xl shadow-lg overflow-hidden z-50" data-testid="global-search-panel">
          {busy && <div className="px-4 py-3 text-sm text-[#52525B]">Searching…</div>}
          {!busy && results && total === 0 && <div className="px-4 py-3 text-sm text-[#52525B]">No matches for "{q}"</div>}
          {!busy && results && (
            <div className="max-h-[420px] overflow-y-auto divide-y divide-[#E4E4E7]">
              {results.customers?.length > 0 && (
                <div>
                  <div className="px-4 py-2 text-[10px] font-semibold tracking-[0.15em] uppercase text-[#52525B] bg-[#F4F4F5]">Customers · {results.customers.length}</div>
                  {results.customers.map((c) => (
                    <button key={c.id} onClick={() => go(`/admin/customers/${c.id}`)} className="w-full text-left px-4 py-2.5 hover:bg-[#EBF2FF] flex items-center gap-2" data-testid={`gs-cust-${c.id}`}>
                      <User size={14} className="text-[#0052FF] shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-[#06152D] truncate">{c.full_name}</div>
                        <div className="text-xs text-[#52525B] font-mono-num">{c.customer_id} · {c.phone}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {results.leads?.length > 0 && (
                <div>
                  <div className="px-4 py-2 text-[10px] font-semibold tracking-[0.15em] uppercase text-[#52525B] bg-[#F4F4F5]">Leads · {results.leads.length}</div>
                  {results.leads.map((l) => (
                    <button key={l.id} onClick={() => go(`/admin/leads`)} className="w-full text-left px-4 py-2.5 hover:bg-[#EBF2FF] flex items-center gap-2" data-testid={`gs-lead-${l.id}`}>
                      <UserPlus size={14} className="text-[#F59E0B] shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-[#06152D] truncate">{l.full_name}</div>
                        <div className="text-xs text-[#52525B] font-mono-num">{l.lead_id} · {l.phone} · {l.status}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {results.loans?.length > 0 && (
                <div>
                  <div className="px-4 py-2 text-[10px] font-semibold tracking-[0.15em] uppercase text-[#52525B] bg-[#F4F4F5]">Loan Files · {results.loans.length}</div>
                  {results.loans.map((l) => (
                    <button key={l.id} onClick={() => go(`/admin/customers/${l.customer_id}`)} className="w-full text-left px-4 py-2.5 hover:bg-[#EBF2FF] flex items-center gap-2" data-testid={`gs-loan-${l.id}`}>
                      <FileText size={14} className="text-[#059669] shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-[#06152D] font-mono-num">{l.file_number || l.application_id}</div>
                        <div className="text-xs text-[#52525B]">{l.loan_type?.replace(/_/g, " ")} · ₹{Number(l.amount || 0).toLocaleString("en-IN")} · {l.status}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {results.insurance?.length > 0 && (
                <div>
                  <div className="px-4 py-2 text-[10px] font-semibold tracking-[0.15em] uppercase text-[#52525B] bg-[#F4F4F5]">Insurance · {results.insurance.length}</div>
                  {results.insurance.map((i) => (
                    <button key={i.id} onClick={() => go(`/admin/customers/${i.customer_id}`)} className="w-full text-left px-4 py-2.5 hover:bg-[#EBF2FF] flex items-center gap-2" data-testid={`gs-ins-${i.id}`}>
                      <Shield size={14} className="text-[#7C3AED] shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-[#06152D] font-mono-num">{i.vehicle_number || i.policy_number || "—"}</div>
                        <div className="text-xs text-[#52525B]">{i.policy_type} · {i.provider} · exp {i.end_date || "—"}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
